// One definition of "a valid threshold number" for every Warning Dashboard config screen (BUG-03).
// Mirrors node-portal src/controller/warningDashboard/thresholdValidation.shared.js, which is the
// real gatekeeper - this just tells the Finance user what is wrong before anything is sent.
//
// The old fields were bare <TextField type="number"> boxes: they took blanks (saved as 0, which
// silently turns a "warns below" rule off), negatives, and percentages over 100.

// The caps are what the server's column can hold (decimal(10,2) / (12,2) / (14,2), INT) - the same numbers as
// thresholdValidation.shared.js, so a 20-digit paste is refused here, before any request, not by a 400 afterwards.
//   nonNegative   = decimal(12,2)  - a count / days / rupee threshold
//   nonNegative10 = decimal(10,2)  - Project ACL / ATP
//   nonNegative14 = decimal(14,2)  - OGD squad, marketing expense, slab target revenue
export const RULES = {
  percent: { min: 0, max: 100, unit: '%' },
  nonNegative: { min: 0, max: 9999999999.99 },
  nonNegative10: { min: 0, max: 99999999.99 },
  nonNegative14: { min: 0, max: 999999999999.99 },
  wholeNumber: { min: 0, max: 2147483647, integer: true },
  positiveHours: { positive: true, max: 2147483647, integer: true, unit: ' hours' },
};

const isBlank = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

// Typed as a plain decimal. JavaScript's Number() also reads '1e3', '1E3', '0x10', '0b11', '+5' and '5.' as numbers;
// none of those is meant in a threshold box, so they are refused rather than stored as 1000 / 16 / 3.
const PLAIN_NUMBER = /^-?(\d+(\.\d+)?|\.\d+)$/;

// '' when fine, otherwise a message. Same wording as the server.
export const validateNumber = (value, label, { min = 0, max = null, integer = false, positive = false, unit = '' } = {}) => {
  if (isBlank(value)) return `${label} is required`;
  if (typeof value === 'string' && !PLAIN_NUMBER.test(value.trim()) && Number.isFinite(Number(value))) {
    return `${label} must be a plain number, like 40 or 37.5 (no letters or e-notation)`;
  }
  const n = typeof value === 'string' ? Number(value.trim()) : Number(value);
  if (!Number.isFinite(n)) return `${label} must be a number`;
  if (positive ? n <= 0 : n < min) return positive ? `${label} must be greater than 0` : n < 0 ? `${label} cannot be negative` : `${label} must be at least ${min}${unit}`;
  if (max !== null && n > max) return `${label} cannot be more than ${max.toLocaleString('en-IN')}${unit}`;
  if (integer && !Number.isInteger(n)) return `${label} must be a whole number`;
  return '';
};

// Keys a number box lets through that no threshold can use: a minus sign, a plus sign and the
// exponent 'e' (so "1e999" can't be typed), and the decimal point on whole-number fields.
const blockedKeys = (rule) => ['-', '+', 'e', 'E', ...(rule.integer ? ['.', ','] : [])];

/**
 * Everything one threshold box needs.
 *   const f = numberField(form.threshold_value, 'Threshold', RULES.percent, submitted);
 *   <TextField value={...} onChange={...} {...f.props} />     and in the save handler:  if (f.error) return;
 * Pass { helper: false } as the 5th argument when the screen renders `error` itself.
 * A blank box only complains after the first Save attempt (`submitted`); anything typed that is
 * wrong complains straight away.
 */
export const numberField = (value, label, rule, submitted = false, { helper = true } = {}) => {
  const error = isBlank(value) && !submitted ? '' : validateNumber(value, label, rule);
  return {
    error,
    props: {
      type: 'number',
      error: Boolean(error),
      // helper:false = the screen shows `error` itself (a narrow inline box has no room for the text)
      helperText: helper ? error || undefined : undefined,
      inputProps: { min: rule.positive ? 1 : rule.min ?? 0, max: rule.max ?? undefined, step: rule.integer ? 1 : 'any' },
      onKeyDown: (e) => {
        if (blockedKeys(rule).includes(e.key)) e.preventDefault();
      },
      // scrolling over a focused number box silently changes it - never on a threshold
      onWheel: (e) => e.target.blur(),
    },
  };
};

// First message in a list of numberField() results - "can I save?"
export const firstError = (...fields) => fields.map((f) => f.error).find(Boolean) || '';

// ---- Target slabs: six fields on one row, plus a rule that spans two of them ----
export const SLAB_FIELDS = {
  target_min: { label: 'Target Revenue from', rule: RULES.nonNegative14 },
  target_max: { label: 'Target Revenue up to', rule: RULES.nonNegative14, optional: true }, // blank = no upper bound
  tar_threshold: { label: 'TAR threshold', rule: RULES.percent },
  arpu_threshold: { label: 'ARPU threshold', rule: RULES.nonNegative },
  admission_count_threshold: { label: 'Admission Count threshold', rule: RULES.wholeNumber },
  lcrr_threshold: { label: 'LCRR threshold', rule: RULES.percent },
};

// { fieldName: message } for everything wrong with one slab row ({} = fine). A blank is always an
// error here except 'up to', which is allowed to be blank on purpose.
export const validateSlab = (values) => {
  const errors = {};
  Object.entries(SLAB_FIELDS).forEach(([key, { label, rule, optional }]) => {
    if (optional && isBlank(values[key])) return;
    const message = validateNumber(values[key], label, rule);
    if (message) errors[key] = message;
  });
  if (!errors.target_min && !errors.target_max && !isBlank(values.target_max) && Number(values.target_max) <= Number(values.target_min)) {
    errors.target_max = "Target Revenue 'up to' must be more than 'from'";
  }
  return errors;
};

// Props for one slab box. A blank box stays quiet until a Save attempt (`submitted`); a wrong value
// complains at once. `hint` is the grey helper line shown while there is no error.
export const slabFieldProps = (key, values, submitted, hint) => {
  const error = submitted || !isBlank(values[key]) ? validateSlab(values)[key] || '' : '';
  const base = numberField(values[key], SLAB_FIELDS[key].label, SLAB_FIELDS[key].rule, true).props;
  return { ...base, error: Boolean(error), helperText: error || hint || undefined };
};
